import { useEffect, useRef, useState, type ReactNode } from "react";
import { RefreshCw, type LucideIcon } from "lucide-react";
import { useTranslations } from "use-intl";
import { cn } from "@/utils";
import { Button } from "@/components/ui/button";

export interface PageHeaderProps {
  /** Page icon shown on the left side; falls back to no icon. */
  icon?: LucideIcon;
  title: ReactNode;
  description?: ReactNode;
  /** Right-side action slot (primary button, etc). */
  actions?: ReactNode;
  /** Optional reload handler. Renders a reload button when present. */
  onReload?: () => Promise<unknown> | void;
  /** External "fetching" signal — keeps the spinner spinning during refetch. */
  isReloading?: boolean;
  /** Optional total + label shown next to the title (e.g. "Found 1,234 logs"). */
  totalLabel?: ReactNode;
  /** Optional badge slot (e.g. "Admin View"). */
  badge?: ReactNode;
  /** Reload safety timeout — force-stop spinner after N ms even if promise hangs. */
  reloadTimeoutMs?: number;
  /** Visible reload button label. Pass i18n from page namespace or common labels. */
  reloadLabel?: string;
  /** Accessible reload label. Defaults to `reloadLabel`. */
  reloadAriaLabel?: string;
  /** Minimum spinner visible duration. Prevents fast BE responses (<100ms)
   *  from making the icon flash imperceptibly on click. */
  minSpinMs?: number;
  className?: string;
  /** Optional content rendered below the header row (e.g. tab strip). */
  children?: ReactNode;
}

const DEFAULT_RELOAD_TIMEOUT = 15_000;
const DEFAULT_MIN_SPIN = 700;

/**
 * PageHeader — unified page-level hero used across all list/detail pages.
 *
 * Style follows the Dashboard "welcome" card pattern: subtle radial primary
 * gradient on `--card`, decorative blur, no hardcoded colors. Replaces the
 * per-page gradient banners (rose, emerald, purple…) we had drifting across
 * the admin.
 *
 * Reload semantics: spinner shows when either (a) parent passes
 * `isReloading=true` or (b) the local timer is still running. The local timer
 * starts on click and auto-resolves after `reloadTimeoutMs` so a hung
 * promise can never leave the icon spinning forever.
 */
export function PageHeader({
  icon: Icon,
  title,
  description,
  actions,
  onReload,
  isReloading = false,
  totalLabel,
  badge,
  reloadTimeoutMs = DEFAULT_RELOAD_TIMEOUT,
  reloadLabel,
  reloadAriaLabel,
  minSpinMs = DEFAULT_MIN_SPIN,
  className,
  children,
}: PageHeaderProps) {
  const tCommon = useTranslations("common");
  const [localSpin, setLocalSpin] = useState(false);
  const safetyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (safetyTimerRef.current) clearTimeout(safetyTimerRef.current);
    };
  }, []);

  const spinning = localSpin || isReloading;
  const resolvedReloadLabel = reloadLabel ?? tCommon("reload");

  async function handleReload() {
    if (!onReload || spinning) return;
    setLocalSpin(true);
    const startedAt = Date.now();
    if (safetyTimerRef.current) clearTimeout(safetyTimerRef.current);
    safetyTimerRef.current = setTimeout(
      () => setLocalSpin(false),
      reloadTimeoutMs
    );
    try {
      await onReload();
    } finally {
      // Always honor the minimum spin duration so a sub-100ms BE response
      // still gives visual feedback. Safety timeout above covers hung
      // promises.
      const elapsed = Date.now() - startedAt;
      const remaining = Math.max(0, minSpinMs - elapsed);
      window.setTimeout(() => {
        if (safetyTimerRef.current) clearTimeout(safetyTimerRef.current);
        setLocalSpin(false);
      }, remaining);
    }
  }

  return (
    <section
      className={cn(
        "relative overflow-hidden rounded-2xl border border-[var(--border)] bg-[var(--card)] p-5 sm:p-6",
        className
      )}
      style={{
        backgroundImage:
          "radial-gradient(ellipse at top right, color-mix(in oklch, var(--primary) 14%, transparent) 0%, transparent 60%)",
      }}
    >
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -top-20 -right-20 size-64 rounded-full opacity-40 blur-3xl"
        style={{ backgroundColor: "var(--primary-100)" }}
      />

      <div className="relative flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3 min-w-0">
          {Icon && (
            <div className="hidden sm:flex size-11 shrink-0 items-center justify-center rounded-xl bg-[var(--primary)]/10 text-[var(--primary)]">
              <Icon className="size-5" />
            </div>
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-semibold tracking-tight text-[var(--foreground)] sm:text-2xl">
                {title}
              </h1>
              {badge}
            </div>
            {description && (
              <p className="mt-1 text-sm text-[var(--muted-foreground)]">
                {description}
              </p>
            )}
            {totalLabel && (
              <div className="mt-2 flex items-center gap-2 text-xs text-[var(--muted-foreground)]">
                {totalLabel}
                {spinning && <RefreshCw className="size-3.5 animate-spin" />}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {actions}
          {onReload && (
            <Button
              type="button"
              onClick={handleReload}
              disabled={spinning}
              aria-label={reloadAriaLabel ?? resolvedReloadLabel}
              variant="outline"
              size="md"
              startIcon={
                <RefreshCw
                  className={cn("size-4", spinning && "animate-spin")}
                />
              }
            >
              <span className="hidden sm:inline">{resolvedReloadLabel}</span>
            </Button>
          )}
        </div>
      </div>

      {children && <div className="relative mt-4">{children}</div>}
    </section>
  );
}
